import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  Stack,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import {
  Person,
  Lock,
  Visibility,
  VisibilityOff,
  ArrowForward,
  LocalHospital,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { styled } from '@mui/material/styles';

// Split Screen Container
const LoginContainer = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  overflow: 'hidden',
});

// Left Panel - Branding
const LeftPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 45%',
  background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(8),
  color: 'white',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-50%',
    width: '100%',
    height: '100%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
    animation: 'float 20s ease-in-out infinite',
  },
  '@keyframes float': {
    '0%, 100%': {
      transform: 'translate(0, 0) rotate(0deg)',
    },
    '33%': {
      transform: 'translate(30px, -30px) rotate(120deg)',
    },
    '66%': {
      transform: 'translate(-20px, 20px) rotate(240deg)',
    },
  },
}));

// Right Panel - Form
const RightPanel = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  background: '#ffffff',
  position: 'relative',
}));

// Logo Container
const LogoBox = styled(Box)({
  width: 140,
  height: 140,
  borderRadius: '30px',
  background: '#ffffff',
  backdropFilter: 'blur(10px)',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '2rem',
  marginLeft: 'auto',
  marginRight: 'auto',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
  overflow: 'hidden',
});

// Modern Input Field
const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#f8fafc',
    fontSize: '0.95rem',
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: '#e2e8f0',
      borderWidth: '1px',
    },
    '&:hover': {
      backgroundColor: '#f1f5f9',
      '& fieldset': {
        borderColor: '#cbd5e1',
      },
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#3b82f6',
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.95rem',
    '&.Mui-focused': {
      color: '#3b82f6',
    },
  },
}));

// Primary Button
const PrimaryButton = styled(Button)({
  borderRadius: '12px',
  padding: '14px 28px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
    transform: 'translateY(-2px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
});

// Decorative Shape
const DecorativeShape = styled(motion.div)({
  position: 'absolute',
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.05)',
  pointerEvents: 'none',
});

const LoginForm = ({ onLogin }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [systemConfig, setSystemConfig] = useState({
    system_logo: null,
    system_name: 'Plataforma Regulatoria',
    system_subtitle: 'Sistema de Gestión Farmacéutica Profesional',
  });

  useEffect(() => {
    loadSystemConfig();
  }, []);

  const loadSystemConfig = async () => {
    try {
      const response = await fetch('/api/system-config');

      if (response.ok) {
        const data = await response.json();
        setSystemConfig({
          system_logo: data.system_logo || null,
          system_name: data.system_name || 'Plataforma Regulatoria',
          system_subtitle: data.system_subtitle || 'Sistema de Gestión Farmacéutica Profesional',
        });
      }
    } catch (error) {
      console.error('Error loading system config:', error);
    }
  };

  const validationSchema = Yup.object({
    username: Yup.string()
      .min(3, 'Mínimo 3 caracteres')
      .required('El usuario es requerido'),
    password: Yup.string()
      .min(6, 'Mínimo 6 caracteres')
      .required('La contraseña es requerida'),
  });

  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      setError('');

      try {
        const response = await fetch('/api/auth/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });

        const data = await response.json();

        if (response.ok) {
          localStorage.setItem('token', data.token);
          localStorage.setItem('currentUser', JSON.stringify(data.user));
          onLogin(data.user);
        } else {
          throw new Error(data.error || 'Credenciales inválidas');
        }
      } catch (error) {
        setError(error.message || 'Error al iniciar sesión');
      } finally {
        setIsLoading(false);
      }
    },
  });

  const shapes = [
    { size: 300, x: '-10%', y: '10%' },
    { size: 200, x: '80%', y: '70%' },
    { size: 150, x: '60%', y: '-5%' },
  ];

  return (
    <LoginContainer>
      {/* Left Panel - Branding */}
      <LeftPanel>
        {/* Decorative Shapes */}
        {shapes.map((shape, index) => (
          <DecorativeShape
            key={index}
            style={{
              width: shape.size,
              height: shape.size,
              left: shape.x,
              top: shape.y,
            }}
            animate={{
              y: [0, -20, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 8 + index * 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        ))}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}
        >
          <LogoBox>
            {systemConfig.system_logo ? (
              <Box
                component="img"
                src={systemConfig.system_logo}
                alt="Logo"
                sx={{
                  maxWidth: '90%',
                  maxHeight: '90%',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <LocalHospital sx={{ fontSize: 70, color: 'white' }} />
            )}
          </LogoBox>

          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
              fontSize: { xs: '2rem', md: '3rem' },
              textShadow: '0 2px 20px rgba(0,0,0,0.2)',
            }}
          >
            {systemConfig.system_name.split(' ').map((word, index) => (
              <React.Fragment key={index}>
                {word}
                {index < systemConfig.system_name.split(' ').length - 1 && <br />}
              </React.Fragment>
            ))}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              opacity: 0.9,
              fontWeight: 400,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            {systemConfig.system_subtitle}
          </Typography>

          <Box sx={{ mt: 6, opacity: 0.7 }}>
            <Typography variant="body2">
              © 2025 Todos los derechos reservados
            </Typography>
          </Box>
        </motion.div>
      </LeftPanel>

      {/* Right Panel - Form */}
      <RightPanel>
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          style={{ width: '100%', maxWidth: 450 }}
        >
          <Box sx={{ mb: 5 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#1e293b',
                mb: 1,
              }}
            >
              Bienvenido de vuelta
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Ingresa tus credenciales para continuar
            </Typography>
          </Box>

          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, fontWeight: 500, color: '#475569' }}
                >
                  Usuario
                </Typography>
                <ModernTextField
                  fullWidth
                  id="username"
                  name="username"
                  placeholder="Ingresa tu usuario"
                  variant="outlined"
                  value={formik.values.username}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.username && Boolean(formik.errors.username)}
                  helperText={formik.touched.username && formik.errors.username}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: '#94a3b8', fontSize: 22 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#475569' }}>
                    Contraseña
                  </Typography>
                  <Link
                    href="#"
                    variant="body2"
                    sx={{
                      color: '#3b82f6',
                      textDecoration: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </Box>
                <ModernTextField
                  fullWidth
                  id="password"
                  name="password"
                  placeholder="Ingresa tu contraseña"
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: '#94a3b8', fontSize: 22 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: '#94a3b8' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{
                      color: '#cbd5e1',
                      '&.Mui-checked': {
                        color: '#3b82f6',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Recordar mis credenciales
                  </Typography>
                }
              />

              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <Alert
                      severity="error"
                      sx={{
                        borderRadius: '12px',
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#991b1b',
                        '& .MuiAlert-icon': {
                          color: '#dc2626',
                        },
                      }}
                    >
                      {error}
                    </Alert>
                  </motion.div>
                )}
              </AnimatePresence>

              <PrimaryButton
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                endIcon={!isLoading && <ArrowForward />}
                sx={{ mt: 2 }}
              >
                {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
              </PrimaryButton>
            </Stack>
          </form>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              ¿No tienes una cuenta?{' '}
              <Link
                href="#"
                sx={{
                  color: '#3b82f6',
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Contacta al administrador
              </Link>
            </Typography>
          </Box>
        </motion.div>
      </RightPanel>
    </LoginContainer>
  );
};

export default LoginForm;
